import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Me } from './contexts';
import { type Db, DbProvider } from './db';

export type Step = 'loading' | 'login' | 'mfa' | 'ready';

/** Which screen the user must see. Staff without a TOTP-verified session never reach the app. */
export function requiredStep(state: { loading: boolean; hasSession: boolean; me?: Me }): Step {
  if (state.loading) return 'loading';
  if (!state.hasSession) return 'login';
  if (!state.me) return 'loading';
  if (state.me.mfa_required && state.me.aal !== 'aal2') return 'mfa';
  return 'ready';
}

interface AuthValue {
  supabase: Db;
  session: Session | null;
  me?: Me;
  step: Step;
  error?: Error;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** The `aal` claim of an access token (aal1 / aal2), without verifying it: only a cache key. */
export function assuranceLevel(token: string | undefined): string | undefined {
  const payload = token?.split('.')[1];
  if (!payload) return undefined;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { aal?: string }).aal;
  } catch {
    return undefined;
  }
}

export function AuthProvider({
  supabase,
  beforeSignOut,
  children,
}: {
  supabase: Db;
  /** e.g. the phone stops receiving this account's pushes; never blocks signing out */
  beforeSignOut?: () => Promise<void>;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setInitializing(false);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      void queryClient.invalidateQueries({ queryKey: ['auth'] });
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase, queryClient]);

  // keyed by the person and the 2FA level: after 2FA (aal2) `me` is re-read, while a routine
  // token refresh keeps the stored copy usable offline
  const meQuery = useQuery({
    queryKey: ['auth', 'me', session?.user.id, assuranceLevel(session?.access_token)],
    enabled: session !== null,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('me');
      if (error) throw error;
      return data as unknown as Me;
    },
  });

  const value = useMemo<AuthValue>(
    () => ({
      supabase,
      session,
      ...(meQuery.data !== undefined && { me: meQuery.data }),
      step: requiredStep({
        loading: initializing || (session !== null && meQuery.isPending),
        hasSession: session !== null,
        ...(meQuery.data !== undefined && { me: meQuery.data }),
      }),
      ...(meQuery.error !== null && { error: meQuery.error }),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['auth'] });
      },
      signOut: async () => {
        await beforeSignOut?.().catch(() => undefined);
        await supabase.auth.signOut();
        queryClient.clear();
      },
    }),
    [
      supabase,
      beforeSignOut,
      session,
      initializing,
      meQuery.data,
      meQuery.isPending,
      meQuery.error,
      queryClient,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      <DbProvider client={supabase}>{children}</DbProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
